// /js/locales/index.js

import { createI18n, useI18n} from 'vue-i18n';
import esMX from './es-MX.json';
import enUS from './en-US.json';
import enJsLocale from './en.js';
import esJsLocale from './es.js';

// Default configuration
const defaultLocale = 'es-MX';
const fallbackLocale = 'en-US';
const REMOTE_LOCALE = false; // Controls whether to fetch languages from the server

/**
 * Merge two message trees, JS winning leaf by leaf.
 *
 * This USED to be a spread — `{ ...esMX, ...esJsLocale }` — and a spread is one
 * level deep, so any top-level namespace declared in BOTH files had its whole
 * JSON subtree REPLACED rather than merged. Six namespaces collide in es-MX
 * (global, sideBar, validation, salonWizard, services, salonConfig) and five in
 * en-US, which silently deleted 161 and 125 leaf keys respectively. The en-US
 * fallback rescued none of them, because the same collision removed them there
 * too — 125 keys resolved to nothing in either locale and would have rendered as
 * the raw key on screen. `missingWarn: false` below meant none of it was ever
 * reported (#103).
 *
 * Deep merging is purely additive: the JS file still wins on every leaf it
 * declares, so nothing that renders today changes — only JSON-only leaves come
 * back. Verified against the real files: es-MX 945 → 1051 leaves, en-US 969 →
 * 1039, zero keys removed, zero values changed. `i18n.keys.test.js` pins it.
 */
const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

function deepMergeMessages(base, override) {
  const merged = { ...base };
  for (const [key, value] of Object.entries(override ?? {})) {
    merged[key] =
      isPlainObject(value) && isPlainObject(merged[key])
        ? deepMergeMessages(merged[key], value)
        : value;
  }
  return merged;
}

// Merge JSON and JS locales
const localLocales = {
  'es-MX': deepMergeMessages(esMX, esJsLocale),
  'en-US': deepMergeMessages(enUS, enJsLocale),
};

// Valid remote locales (only used if REMOTE_LOCALE is true)
const remoteLocales = ['es-CL', 'es-CO', 'es-AR', 'es-ES', 'en-US', 'es-MX'];

/**
 * Determines the user's preferred locale based on localStorage, navigator.language, or defaults.
 * If the locale is invalid, it falls back to the default locale.
 *
 * @returns {string} - The determined locale.
 */
function determineUserLocale() {
  const storedLocale = localStorage.getItem('LOCAL-LANG');
  let userLocale = storedLocale || navigator.language || defaultLocale;

  // Normalize the locale (e.g., "es" -> "es-MX", "en" -> "en-US")
  userLocale = normalizeLocale(userLocale);

  // Ensure the locale is valid
  if (!localLocales[userLocale] && !REMOTE_LOCALE) {
    console.warn(`Locale ${userLocale} not found locally. Falling back to ${defaultLocale}`);
    userLocale = defaultLocale;
  }
  console.debug("userLocale", userLocale);
  window["$userLocale"] = userLocale;
  return userLocale;
}

/**
 * Normalizes the locale to standard formats.
 * - Converts "es" to "es-MX".
 * - Converts "en" to "en-US".
 * - Unsupported locales adjust to the most relevant one.
 *
 * @param {string} locale - The locale to normalize.
 * @returns {string} - The normalized locale.
 */
function normalizeLocale(locale) {
  const [language, region] = locale.split('-');
  const normalized = `${language.toLowerCase()}-${(region || defaultLocale.split('-')[1]).toUpperCase()}`;

  if (language === 'es' && !remoteLocales.includes(normalized)) return 'es-MX';
  if (language === 'en') return 'en-US';
  return normalized;
}

/**
 * Fetches a remote locale from the server.
 *
 * @param {string} locale - The locale to fetch.
 * @returns {Promise<object|null>} - The locale messages or null if fetching fails.
 */
async function fetchLocaleMessages(locale) {
  const url = `/locales/${locale}.json`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch locale from ${url}:`, error);
    return null;
  }
}

/**
 * Changes the active language dynamically.
 * Uses determineUserLocale to normalize and validate the locale,
 * and updates i18n with the appropriate messages.
 *
 * @returns {Promise<void>}
 */
async function changeLanguage(newLocale) {
  const normalizedLocale = normalizeLocale(newLocale);

  // If the locale is already active, do nothing
  if (i18n.global.locale.value === normalizedLocale) {
    console.debug(`Locale ${normalizedLocale} is already active.`);
    return;
  }

  // Attempt to load the new locale
  if (REMOTE_LOCALE) {
    try {
      const remoteMessages = await fetchLocaleMessages(normalizedLocale);
      if (remoteMessages) {
        // Merge the new translations into the existing object
        Object.keys(remoteMessages).forEach((key) => {
          i18n.global.messages.value[normalizedLocale][key] = remoteMessages[key];
        });
      }
    } catch (error) {
      console.error(`Failed to load remote locale ${normalizedLocale}:`, error);
    }
  }

  // Fallback to local or default locale if the remote one fails
  if (!localLocales[normalizedLocale]) {
    console.warn(`Locale ${normalizedLocale} not found. Falling back to default.`);
    i18n.global.locale.value = defaultLocale;
    return;
  }


  i18n.global.setLocaleMessage(normalizedLocale, localLocales[normalizedLocale]);

  // Set the new locale
  i18n.global.locale.value = normalizedLocale;
  console.debug(`Locale changed to: ${normalizedLocale}`);
  window["$userLocale"] = normalizedLocale;
  window.i18n=i18n
}

/**
 * Validates and sets the user's preferred locale in localStorage.
 * Ensures the locale follows the format "language-REGION".
 *
 * @param {string} newLocale - The locale to set.
 */
function setStorageUserLanguage(newLocale) {
  if (!newLocale || typeof newLocale !== 'string') {
    console.error('Invalid locale provided.');
    return;
  }

  const [language, region] = newLocale.split('-');

  // Validate and normalize locale
  if (!language || !region) {
    console.warn(`Invalid locale format "${newLocale}". Defaulting to ${defaultLocale}.`);
    newLocale = defaultLocale;
  } else {
    newLocale = `${language.toLowerCase()}-${region.toUpperCase()}`;
  }

  // Check if the locale is valid
  if (!localLocales[newLocale] && !REMOTE_LOCALE) {
    console.warn(`Locale ${newLocale} is not valid locally. Defaulting to ${defaultLocale}.`);
    newLocale = defaultLocale;
  }

  // Save to localStorage
  localStorage.setItem('LOCAL-LANG', newLocale);
}

// Create the i18n instance using Composition API mode
const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: determineUserLocale(), // Now this is synchronous
  fallbackLocale,
  messages: localLocales,
  missingWarn: false,
  fallbackWarn: false,
  warnHtmlMessage: false, // Disable warnings globally for HTML messages
});

export { i18n, determineUserLocale, fetchLocaleMessages, changeLanguage, setStorageUserLanguage, useI18n, localLocales, deepMergeMessages };
